import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Volume2, Play, Pause, StopCircle, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

/**
 * PassageReader
 * - Renders a "Read to Me" toolbar (Play / Pause / Resume / Stop + speed selector)
 * - Uses the browser Web Speech API (SpeechSynthesis)
 * - Highlights the current sentence as it's read aloud
 *
 * Props:
 *   text:       the passage text to render and read
 *   className:  optional wrapper className
 *   textClassName: optional className for the rendered text container
 */
const SPEEDS = [
  { value: '0.8', label: '0.8x' },
  { value: '1', label: '1x' },
  { value: '1.2', label: '1.2x' },
];

// Split text into sentences while preserving original character offsets so we can map
// SpeechSynthesisUtterance onboundary char indexes back to the matching sentence.
const splitIntoSentencesWithOffsets = (text) => {
  if (!text) return [];
  const result = [];
  // Match non-empty runs ending in . ! ? OR end-of-string. Capture trailing whitespace too.
  const re = /[^.!?]+[.!?]+(?:\s+|$)|[^.!?]+$/g;
  let m;
  while ((m = re.exec(text)) !== null) {
    const start = m.index;
    const end = start + m[0].length;
    const sentence = m[0];
    if (sentence.trim()) {
      result.push({ start, end, text: sentence });
    }
  }
  return result;
};

const pickEnglishVoice = (voices) => {
  if (!voices || voices.length === 0) return null;
  // Prefer en-US, then any en-*, then default
  const enUS = voices.find((v) => /^en[-_]US/i.test(v.lang));
  if (enUS) return enUS;
  const en = voices.find((v) => /^en/i.test(v.lang));
  if (en) return en;
  return voices.find((v) => v.default) || voices[0];
};

const isSpeechSupported = () =>
  typeof window !== 'undefined' && 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window;

export const PassageReader = ({ text, className = '', textClassName = '' }) => {
  const supported = isSpeechSupported();
  const sentences = useMemo(() => splitIntoSentencesWithOffsets(text || ''), [text]);

  const [status, setStatus] = useState('idle'); // 'idle' | 'playing' | 'paused'
  const [speed, setSpeed] = useState('1');
  const [currentSentence, setCurrentSentence] = useState(-1);
  const [voice, setVoice] = useState(null);
  const utteranceRef = useRef(null);

  // Load voices (some browsers populate them async)
  useEffect(() => {
    if (!supported) return;
    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      setVoice(pickEnglishVoice(voices));
    };
    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
    return () => {
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, [supported]);

  // Cleanup on unmount or when text changes
  useEffect(() => {
    return () => {
      if (supported) {
        window.speechSynthesis.cancel();
      }
    };
  }, [supported]);

  useEffect(() => {
    // If text changes mid-playback, stop.
    if (supported) window.speechSynthesis.cancel();
    setStatus('idle');
    setCurrentSentence(-1);
  }, [text, supported]);

  const findSentenceIndex = (charIndex) => {
    for (let i = 0; i < sentences.length; i++) {
      if (charIndex >= sentences[i].start && charIndex < sentences[i].end) return i;
    }
    return sentences.length - 1;
  };

  const handlePlay = () => {
    if (!supported || !text) return;
    window.speechSynthesis.cancel();

    const utt = new SpeechSynthesisUtterance(text);
    utt.lang = 'en-US';
    utt.rate = parseFloat(speed);
    utt.pitch = 1;
    if (voice) utt.voice = voice;

    utt.onboundary = (event) => {
      // Some browsers only fire word boundaries; map charIndex to sentence index.
      if (typeof event.charIndex === 'number') {
        const idx = findSentenceIndex(event.charIndex);
        setCurrentSentence(idx);
      }
    };
    utt.onstart = () => {
      setStatus('playing');
      setCurrentSentence(0);
    };
    utt.onpause = () => setStatus('paused');
    utt.onresume = () => setStatus('playing');
    utt.onend = () => {
      setStatus('idle');
      setCurrentSentence(-1);
    };
    utt.onerror = () => {
      setStatus('idle');
      setCurrentSentence(-1);
    };

    utteranceRef.current = utt;
    window.speechSynthesis.speak(utt);
    // Optimistic UI: flip to "playing" immediately so Pause is visible without waiting for onstart.
    setStatus('playing');
    setCurrentSentence(0);
  };

  const handlePause = () => {
    if (!supported) return;
    window.speechSynthesis.pause();
    setStatus('paused');
  };

  const handleResume = () => {
    if (!supported) return;
    window.speechSynthesis.resume();
    setStatus('playing');
  };

  const handleStop = () => {
    if (!supported) return;
    window.speechSynthesis.cancel();
    setStatus('idle');
    setCurrentSentence(-1);
  };

  const handleSpeedChange = (val) => {
    setSpeed(val);
    if (status !== 'idle') {
      // Restart so the new rate takes effect immediately
      handleStop();
      setTimeout(handlePlay, 50);
    }
  };

  if (!text) return null;

  return (
    <div className={className} data-testid="passage-reader">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-3 p-2 bg-secondary/40 border border-border rounded-lg">
        <div className="flex items-center gap-1.5 pl-1 text-sm font-medium text-foreground">
          <Volume2 className="w-4 h-4 text-primary" />
          <span>Read to Me</span>
        </div>

        <div className="flex items-center gap-1 ml-1">
          {status === 'idle' && (
            <Button
              size="sm"
              variant="default"
              onClick={handlePlay}
              disabled={!supported}
              className="h-8 gap-1"
              data-testid="passage-reader-play"
            >
              <Play className="w-3.5 h-3.5" />
              Play
            </Button>
          )}
          {status === 'playing' && (
            <Button
              size="sm"
              variant="secondary"
              onClick={handlePause}
              className="h-8 gap-1"
              data-testid="passage-reader-pause"
            >
              <Pause className="w-3.5 h-3.5" />
              Pause
            </Button>
          )}
          {status === 'paused' && (
            <Button
              size="sm"
              variant="default"
              onClick={handleResume}
              className="h-8 gap-1"
              data-testid="passage-reader-resume"
            >
              <Play className="w-3.5 h-3.5" />
              Resume
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={handleStop}
            disabled={!supported || status === 'idle'}
            className="h-8 gap-1"
            data-testid="passage-reader-stop"
          >
            <StopCircle className="w-3.5 h-3.5" />
            Stop
          </Button>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-muted-foreground hidden sm:inline">Speed</span>
          <Select value={speed} onValueChange={handleSpeedChange}>
            <SelectTrigger className="h-8 w-[80px]" data-testid="passage-reader-speed">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SPEEDS.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!supported && (
            <span className="text-xs text-muted-foreground" data-testid="passage-reader-unsupported">
              <RotateCcw className="w-3 h-3 inline mr-1" />
              Voice not supported in this browser
            </span>
          )}
        </div>
      </div>

      {/* Passage with sentence highlighting */}
      <div
        className={`text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap ${textClassName}`}
        data-testid="passage-reader-text"
      >
        {sentences.length > 0 ? (
          sentences.map((s, idx) => (
            <span
              key={idx}
              className={`transition-colors duration-150 rounded-sm ${
                idx === currentSentence ? 'bg-amber-200/70 text-foreground' : ''
              }`}
            >
              {s.text}
            </span>
          ))
        ) : (
          <span>{text}</span>
        )}
      </div>
    </div>
  );
};

export default PassageReader;
